import { isEqual } from "lodash";
import React, { MouseEvent, useState } from "react";

import { Checkbox, Menu, MenuItem, TableCell, TableRow, Tooltip } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from "react-beautiful-dnd";

import { ContextMenuItem } from "types/common-types";
import EnhancedTableCell from "./EnhancedTableCell";
import type { LazyTableRow, TableCellType } from "./index";

type EnhancedTableRowProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLTableRowElement>,
  HTMLTableRowElement
> & {
  row: LazyTableRow;
  index: number;
  hover?: boolean;
  cells: Array<TableCellType>;
  withCheckbox?: boolean;
  handleClick: (event: React.MouseEvent, id: string, row: LazyTableRow) => void;
  isSelected: (id: string) => boolean;
  innerRef: (element?: HTMLElement | null) => any;
  draggableProps: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | undefined;
  dragStarting?: boolean;
  contextMenuItems?: ContextMenuItem[];
  sx?: SxProps<Theme>;
};

const EnhancedTableRow = ({
  row,
  index,
  hover,
  cells,
  withCheckbox,
  handleClick,
  isSelected,
  innerRef,
  draggableProps,
  dragHandleProps,
  dragStarting,
  contextMenuItems,
  sx,
}: EnhancedTableRowProps) => {
  const labelId = `table-checkbox-${row.id}`;
  const isItemSelected = isSelected(row.id);

  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<null | {
    top: number;
    left: number;
  }>(null);

  const handleRightClick = (event: MouseEvent) => {
    if (contextMenuItems) {
      event.preventDefault();
      setContextMenuPosition({ top: event.clientY, left: event.clientX });
      setContextMenuOpen(true);
    }
  };

  const handleContextMenuClose = () => {
    setContextMenuOpen(false);
  };

  return (
    <Tooltip title={row.tooltip}>
      <TableRow
        hover={hover}
        onClick={($e: any) => handleClick($e, row.id, row)}
        onContextMenu={contextMenuItems && handleRightClick}
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        selected={isItemSelected}
        ref={innerRef}
        {...draggableProps}
        {...dragHandleProps}
        data-cy={`${index}GridLink`}
        sx={[{ background: row.color! }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {withCheckbox && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={isItemSelected}
              inputProps={{ "aria-labelledby": labelId }}
            />
          </TableCell>
        )}
        {cells.map((cell, i) => {
          const key = `${row.id}-${i}-${cell.accessor}`;
          return (
            <EnhancedTableCell
              key={key}
              cell={cell}
              row={row}
              dragStarting={dragStarting}
            />
          );
        })}
        {contextMenuItems && (
          <Menu
            open={contextMenuOpen}
            onClose={handleContextMenuClose}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenuPosition !== null
                ? {
                    top: contextMenuPosition.top,
                    left: contextMenuPosition.left,
                  }
                : undefined
            }
          >
            {contextMenuItems.map((item: ContextMenuItem) => (
              <MenuItem
                key={item.key}
                {...item.props}
                onClick={(e: any) => {
                  item.onClick && item.onClick(e, row.id);
                  handleContextMenuClose();
                }}
              >
                {item.content}
              </MenuItem>
            ))}
          </Menu>
        )}
      </TableRow>
    </Tooltip>
  );
};

export default React.memo(EnhancedTableRow, isEqual);
